import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Card, Button, ProgressBar, Alert, Modal, Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Quiz,
  Question,
  QuizAttemptResponse,
  UnlockedAchievementPayload,
  UnlockedCharacterPayload,
  LoginStreakHome,
  HostedOpenAsyncPvPSummary,
  authService,
  pvpService,
  quizService,
  type McFinishAudit,
} from '../services/api';
import QuizFinishCelebrationModal from './QuizFinishCelebrationModal';
import LevelUpCelebrationModal from './LevelUpCelebrationModal';
import { useQuizFinishLevelUp } from '../hooks/useQuizFinishLevelUp';
import { useQuizTimer } from '../hooks/useQuizTimer';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { isPwaStandalone } from '../utils/pwaStandalone';
import SoloQuizResultsModal from './SoloQuizResultsModal';
import { quizQuestionImageUrlsFromKey } from '../utils/quizQuestionImageSrc';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import QuizPlayPageHeading from './QuizPlayPageHeading';
import QuizSoundToggleButton from './QuizSoundToggleButton';
import NavbarMusicToggle from './NavbarMusicToggle';
import { useTranslation } from 'react-i18next';
import { playQuizSfxCorrect, playQuizSfxIncorrect } from '../audio/quizSfx';
import { onQuizSkipClick, useBlurMobileSkipFocusOnQuestionChange } from '../utils/quizSkipButton';
import {
  buildSoloArenaHostCompleteBody,
  mcCorrectPerQuestionForSoloBand,
  type SoloArenaPublishSnapshot,
} from '../utils/soloArenaPublishLayout';
import {
  maxPointsForPlayDifficultyBand,
  normalizePlayDifficultyLabel,
  witcTimeLimitSecondsForBand,
} from '../utils/quizPlayBandScoring';
import { pickBilingualLine } from '../utils/quizDisplayTitle';
import { useMcWasm } from '../hooks/useMcWasm';
import McImageAnswerButton from './McImageAnswerButton';
import { quizQuestionPanelBackground } from '../utils/quizQuestionPanelBackground';
import { WITC_REVEAL_DELAY_MS } from '../utils/witcExpectedAnswer';

import './shared/QuizStyles.css';
import './QuizPlayer.css';

const SOLO_TIME_LIMIT_SECONDS = 30;

export interface McPvpFinishPayload {
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  durationSeconds: number;
  skippedQuestions: number;
  questionIdsInOrder: string[];
  answerIdsPerQuestion: string[][];
  responseTimeSecondsPerQuestion: number[];
  correctPerQuestion: boolean[];
  mcFinishAudit: McFinishAudit;
}

export interface McQuizPlayerProps {
  quizId: string;
  quiz: Quiz;
  questions: Question[];
  startTime: Date;
  mcPlaySessionId?: string | null;
  mcEngineBootstrapCipher?: string | null;
  playDifficulty?: string | null;
  playDifficultyPerQuestion?: string[] | null;
  soloQuickMatch?: boolean;
  onPlayAgain: () => void | Promise<void>;
  playAgainLoading?: boolean;
  suppressResultsModal?: boolean;
  arenaPublishSnapshotRef?: React.MutableRefObject<SoloArenaPublishSnapshot | null>;
  arenaPublishChecked?: boolean;
  onArenaPublishCheckedChange?: (checked: boolean) => void;
  arenaPublishCheckedRef?: React.MutableRefObject<boolean>;
  embeddedInPvp?: boolean;
  onPvpQuitRequest?: () => void;
  pvpAsync?: {
    flow: 'host' | 'opponent';
    roomId: string;
    onComplete: (payload: McPvpFinishPayload) => Promise<void> | void;
  };
}

type PvpMcQuestionSource = {
  id: string;
  text: string;
  textEn?: string | null;
  imageAssetKey?: string | null;
  options: string[];
  optionTextEns?: (string | null)[];
  optionImageAssetKeys?: (string | null)[];
  answerIds?: string[];
};

/** Map PvP room questions (options + answerIds) to solo `Question[]` for WASM gameplay. */
export function mapPvpMcQuestionsToApiQuestions(
  quizId: string,
  pvpQuestions: PvpMcQuestionSource[]
): Question[] {
  return pvpQuestions.map((q) => ({
    id: q.id,
    quizId,
    text: q.text,
    textEn: q.textEn ?? null,
    imageAssetKey: q.imageAssetKey ?? null,
    createdAt: '',
    updatedAt: '',
    answers: (q.answerIds ?? []).map((id, i) => ({
      id,
      questionId: q.id,
      text: q.options[i] ?? '',
      textEn: q.optionTextEns?.[i] ?? null,
      imageAssetKey: q.optionImageAssetKeys?.[i] ?? null,
      isCorrect: false,
      isCorrectEasy: false,
      isCorrectMedium: false,
    })),
  }));
}

const McQuizPlayer: React.FC<McQuizPlayerProps> = ({
  quizId,
  quiz,
  questions,
  startTime,
  mcEngineBootstrapCipher,
  playDifficulty,
  playDifficultyPerQuestion,
  soloQuickMatch = false,
  onPlayAgain,
  playAgainLoading = false,
  suppressResultsModal = false,
  arenaPublishSnapshotRef,
  arenaPublishChecked: arenaPublishCheckedProp,
  onArenaPublishCheckedChange: onArenaPublishCheckedChangeProp,
  arenaPublishCheckedRef: arenaPublishCheckedRefProp,
  embeddedInPvp = false,
  onPvpQuitRequest,
  pvpAsync,
}) => {
  const { t } = useSafeTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Map<number, string>>(new Map());
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [saveAttemptError, setSaveAttemptError] = useState<string | null>(null);
  const [savingQuizAttempt, setSavingQuizAttempt] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultDurationSeconds, setResultDurationSeconds] = useState<number | null>(null);
  const [quizResponse, setQuizResponse] = useState<QuizAttemptResponse | null>(null);
  const [earnedXP, setEarnedXP] = useState(0);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [hasTooManySkips, setHasTooManySkips] = useState(false);
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false);
  const [mcValidateResult, setMcValidateResult] = useState<{
    correct: boolean;
    correctAnswerId?: string;
  } | null>(null);
  const [mcValidating, setMcValidating] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAchievements, setCelebrationAchievements] = useState<UnlockedAchievementPayload[]>([]);
  const [celebrationCharacters, setCelebrationCharacters] = useState<UnlockedCharacterPayload[]>([]);
  const [celebrationLoginStreak, setCelebrationLoginStreak] = useState<LoginStreakHome | null>(null);
  const [celebrationPlayerNick, setCelebrationPlayerNick] = useState('');
  const levelUpCelebration = useQuizFinishLevelUp();
  const [fallbackArenaPublishChecked, setFallbackArenaPublishChecked] = useState(true);
  const fallbackArenaPublishCheckedRef = useRef(true);
  const [hostedOpenSummaryForArena, setHostedOpenSummaryForArena] =
    useState<HostedOpenAsyncPvPSummary | null>(null);
  const [arenaHostedSummaryLoading, setArenaHostedSummaryLoading] = useState(false);
  const [mcSoloImageExt, setMcSoloImageExt] = useState<'webp' | 'png'>('webp');

  const finishQuizInFlightRef = useRef(false);
  const finishQuizRef = useRef<(answersForSubmit: Map<number, string>) => Promise<void>>(async () => {});
  const questionShownAtRef = useRef<number>(Date.now());
  const secondsToAnswerRef = useRef<number[]>([]);
  const revealSnapRef = useRef<{
    questionIndex: number;
    answerId: string;
    validate: { correct: boolean; correctAnswerId?: string };
  } | null>(null);
  const currentQuestionIndexRef = useRef(currentQuestionIndex);
  currentQuestionIndexRef.current = currentQuestionIndex;

  const arenaPublishChecked = arenaPublishCheckedProp ?? fallbackArenaPublishChecked;
  const setArenaPublishChecked = onArenaPublishCheckedChangeProp ?? setFallbackArenaPublishChecked;
  const arenaPublishCheckedRef = arenaPublishCheckedRefProp ?? fallbackArenaPublishCheckedRef;

  const {
    ready: mcWasmReady,
    error: mcWasmError,
    submitAnswer: mcWasmSubmitAnswer,
    buildFinishPayload: mcWasmBuildFinishPayload,
  } = useMcWasm(mcEngineBootstrapCipher ?? undefined);

  const questionTimeLimitSeconds = useMemo(() => {
    if (
      soloQuickMatch &&
      playDifficultyPerQuestion &&
      playDifficultyPerQuestion.length === questions.length &&
      questions.length > 0
    ) {
      return witcTimeLimitSecondsForBand(playDifficultyPerQuestion[currentQuestionIndex]);
    }
    if (embeddedInPvp) {
      return witcTimeLimitSecondsForBand(playDifficulty ?? quiz.difficulty);
    }
    return SOLO_TIME_LIMIT_SECONDS;
  }, [
    soloQuickMatch,
    playDifficultyPerQuestion,
    currentQuestionIndex,
    questions.length,
    embeddedInPvp,
    playDifficulty,
    quiz.difficulty,
  ]);

  const headerDifficultyLabel = useMemo(() => {
    if (
      soloQuickMatch &&
      playDifficultyPerQuestion &&
      playDifficultyPerQuestion.length === questions.length &&
      questions.length > 0
    ) {
      return normalizePlayDifficultyLabel(playDifficultyPerQuestion[currentQuestionIndex]);
    }
    return normalizePlayDifficultyLabel(playDifficulty ?? quiz.difficulty);
  }, [
    soloQuickMatch,
    playDifficultyPerQuestion,
    questions.length,
    currentQuestionIndex,
    playDifficulty,
    quiz.difficulty,
  ]);

  const pointsThisQuestion = useMemo(() => {
    if (
      soloQuickMatch &&
      playDifficultyPerQuestion &&
      playDifficultyPerQuestion.length === questions.length &&
      questions.length > 0
    ) {
      return maxPointsForPlayDifficultyBand(playDifficultyPerQuestion[currentQuestionIndex]);
    }
    if (embeddedInPvp) {
      return maxPointsForPlayDifficultyBand(playDifficulty ?? quiz.difficulty);
    }
    return quiz.scorePerCorrectAnswer ?? 2;
  }, [
    soloQuickMatch,
    playDifficultyPerQuestion,
    questions.length,
    currentQuestionIndex,
    embeddedInPvp,
    playDifficulty,
    quiz.difficulty,
    quiz.scorePerCorrectAnswer,
  ]);

  useEffect(() => {
    arenaPublishCheckedRef.current = arenaPublishChecked;
  }, [arenaPublishChecked, arenaPublishCheckedRef]);

  const arenaPublishOffer = useMemo(
    () =>
      !soloQuickMatch &&
      !embeddedInPvp &&
      isQuizCompleted &&
      !hasTooManySkips &&
      questions.length > 0 &&
      correctAnswers / questions.length >= 0.7,
    [soloQuickMatch, embeddedInPvp, isQuizCompleted, hasTooManySkips, questions.length, correctAnswers]
  );

  const arenaPublishBlockedOpenQuiz = useMemo(() => {
    const qid = quiz?.id?.trim();
    if (!qid || !hostedOpenSummaryForArena?.openQuizIds?.length) return false;
    const lower = qid.toLowerCase();
    return hostedOpenSummaryForArena.openQuizIds.some((id) => id.trim().toLowerCase() === lower);
  }, [quiz?.id, hostedOpenSummaryForArena]);

  const arenaPublishBlockedMaxRooms = useMemo(() => {
    if (!hostedOpenSummaryForArena) return false;
    return hostedOpenSummaryForArena.openCount >= hostedOpenSummaryForArena.maxOpenRooms;
  }, [hostedOpenSummaryForArena]);

  const arenaPublishBlocked = arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms;

  const arenaPublishBlockedReason = arenaPublishBlockedOpenQuiz
    ? ('duplicateQuiz' as const)
    : arenaPublishBlockedMaxRooms
      ? ('maxRooms' as const)
      : undefined;

  useEffect(() => {
    if (arenaPublishBlockedOpenQuiz || arenaPublishBlockedMaxRooms) {
      setArenaPublishChecked(false);
    }
  }, [arenaPublishBlockedOpenQuiz, arenaPublishBlockedMaxRooms, setArenaPublishChecked]);

  useEffect(() => {
    if (!showResultsModal || !arenaPublishOffer || !isQuizCompleted || hasTooManySkips) {
      setHostedOpenSummaryForArena(null);
      setArenaHostedSummaryLoading(false);
      return;
    }
    let cancelled = false;
    setArenaHostedSummaryLoading(true);
    void pvpService
      .getHostedOpenAsyncSummary()
      .then((s) => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(s);
          setArenaHostedSummaryLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHostedOpenSummaryForArena(null);
          setArenaHostedSummaryLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showResultsModal, arenaPublishOffer, isQuizCompleted, hasTooManySkips]);

  const executeArenaPublishFromSoloIfNeeded = useCallback(async () => {
    const snap = arenaPublishSnapshotRef?.current;
    if (!snap || !arenaPublishCheckedRef.current) return;
    const qidForGate = snap.quizId?.trim();
    try {
      const summaryFresh = await pvpService.getHostedOpenAsyncSummary();
      if (summaryFresh.openCount >= summaryFresh.maxOpenRooms) return;
      if (
        qidForGate &&
        summaryFresh.openQuizIds.some((id) => id.trim().toLowerCase() === qidForGate.toLowerCase())
      )
        return;
    } catch {
      return;
    }
    try {
      const band = normalizePlayDifficultyLabel(snap.playDifficulty) || 'Easy';
      const room = await pvpService.createAsyncPvPRoom({
        name: snap.roomDisplayName.trim() || 'PvP',
        quizId: snap.quizId,
        playDifficulty: band,
      });
      await pvpService.completeAsyncHostQuiz(
        room.id,
        buildSoloArenaHostCompleteBody(snap),
        { pwaClient: isPwaStandalone() }
      );
      toast.success(t('quiz.soloArenaPublishSuccess', 'Room listed on the PvP Arena.'));
      if (arenaPublishSnapshotRef) arenaPublishSnapshotRef.current = null;
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) &&
        err.response?.data &&
        typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.soloArenaPublishError', 'Could not publish to the Arena.');
      toast.error(msg);
    }
  }, [t, arenaPublishSnapshotRef]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCelebrationPlayerNick('');
      return;
    }
    authService
      .getProfile()
      .then((u) => {
        const nick = (u.nickname && u.nickname.trim()) || u.name || '';
        setCelebrationPlayerNick(nick);
      })
      .catch(() => setCelebrationPlayerNick(''));
  }, []);

  const mcQuestion = questions[currentQuestionIndex];

  const mcImageUrls = useMemo(() => {
    if (!quiz || !mcQuestion?.imageAssetKey) return null;
    const base =
      mcQuestion.soloQuestionImageBasePath?.trim() || quiz.mcImageAssetsBasePath || '';
    return quizQuestionImageUrlsFromKey(base, mcQuestion.imageAssetKey);
  }, [quiz, mcQuestion?.imageAssetKey, mcQuestion?.soloQuestionImageBasePath]);

  const mcAnswerImageBase = useMemo(() => {
    if (!quiz?.mcUseImageAnswers) return null;
    const p = quiz.mcAnswerImageAssetsBasePath?.trim();
    return p || null;
  }, [quiz?.mcUseImageAnswers, quiz?.mcAnswerImageAssetsBasePath]);

  const mcUsesImageAnswers = useMemo(() => {
    if (!mcAnswerImageBase || !mcQuestion) return false;
    return mcQuestion.answers.some((a) => Boolean(a.imageAssetKey?.trim()));
  }, [mcAnswerImageBase, mcQuestion?.answers]);

  useEffect(() => {
    setMcSoloImageExt('webp');
  }, [currentQuestionIndex, mcImageUrls]);

  const mcPanelBg = useMemo(
    () =>
      mcQuestion ? quizQuestionPanelBackground(currentQuestionIndex, mcQuestion.id) : 'transparent',
    [currentQuestionIndex, mcQuestion?.id]
  );

  const handleSkipQuestion = useCallback(() => {
    if (savingQuizAttempt) return;
    if (answers.get(currentQuestionIndex) !== undefined) return;

    const elapsed = Math.min(
      questionTimeLimitSeconds,
      Math.max(0, Math.floor((Date.now() - questionShownAtRef.current) / 1000))
    );
    secondsToAnswerRef.current[currentQuestionIndex] = elapsed;

    const merged = new Map(answers);
    merged.set(currentQuestionIndex, 'skip');
    setAnswers(new Map(merged));

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((i) => i + 1);
      setSelectedAnswer(null);
      setShowAnswerFeedback(false);
      setMcValidateResult(null);
    } else {
      void finishQuizRef.current(merged);
    }
  }, [savingQuizAttempt, answers, currentQuestionIndex, questions.length, questionTimeLimitSeconds]);

  const handleTimeout = useCallback(() => {
    if (selectedAnswer === null && !isQuizCompleted && !mcValidating) {
      handleSkipQuestion();
    }
  }, [selectedAnswer, isQuizCompleted, mcValidating, handleSkipQuestion]);

  const { timeLeft, resetTimer, stopTimer } = useQuizTimer({
    timeLimit: questionTimeLimitSeconds,
    onTimeout: handleTimeout,
    isActive: mcWasmReady && !isQuizCompleted && currentQuestionIndex < questions.length,
  });

  useEffect(() => {
    if (mcWasmReady && questions.length > 0 && currentQuestionIndex < questions.length) {
      if (answers.get(currentQuestionIndex) === undefined) {
        questionShownAtRef.current = Date.now();
        resetTimer();
      }
    }
  }, [currentQuestionIndex, questions.length, mcWasmReady, answers, questionTimeLimitSeconds, resetTimer]);

  useBlurMobileSkipFocusOnQuestionChange(currentQuestionIndex);

  const handleAnswerSelect = async (answerId: string) => {
    if (savingQuizAttempt || selectedAnswer !== null || mcValidating) return;
    const cq = questions[currentQuestionIndex];
    if (!cq || !mcEngineBootstrapCipher || !mcWasmReady) return;

    const questionIndexWhenTapped = currentQuestionIndex;
    setMcValidating(true);
    try {
      const wasmRes = await mcWasmSubmitAnswer(cq.id, answerId);
      if (questionIndexWhenTapped !== currentQuestionIndexRef.current) return;

      const res = {
        correct: wasmRes?.isCorrect ?? false,
        correctAnswerId: wasmRes?.correctAnswerId ?? undefined,
      };
      if (res.correct) playQuizSfxCorrect();
      else playQuizSfxIncorrect();

      const elapsed = Math.min(
        questionTimeLimitSeconds,
        Math.max(0, Math.floor((Date.now() - questionShownAtRef.current) / 1000))
      );
      secondsToAnswerRef.current[questionIndexWhenTapped] = elapsed;

      revealSnapRef.current = {
        questionIndex: questionIndexWhenTapped,
        answerId,
        validate: res,
      };
      setMcValidateResult(res);
      setSelectedAnswer(answerId);
      setShowAnswerFeedback(true);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? String(err.response?.data?.message ?? err.message)
        : err instanceof Error
          ? err.message
          : 'Unknown error';
      toast.error(msg);
    } finally {
      setMcValidating(false);
    }
  };

  const advanceAfterReveal = async () => {
    if (savingQuizAttempt || !revealSnapRef.current) return;
    const snap = revealSnapRef.current;
    revealSnapRef.current = null;

    const { questionIndex: qi, answerId, validate: val } = snap;
    const merged = new Map(answers);
    merged.set(qi, answerId);
    setAnswers(new Map(merged));

    if (val.correct) {
      const pts =
        soloQuickMatch &&
        playDifficultyPerQuestion &&
        playDifficultyPerQuestion.length === questions.length
          ? maxPointsForPlayDifficultyBand(playDifficultyPerQuestion[qi])
          : embeddedInPvp
            ? maxPointsForPlayDifficultyBand(playDifficulty ?? quiz.difficulty)
            : quiz.scorePerCorrectAnswer ?? 2;
      setScore((prev) => prev + pts);
      setCorrectAnswers((prev) => prev + 1);
    }

    setMcValidateResult(null);
    setSelectedAnswer(null);
    setShowAnswerFeedback(false);

    if (qi < questions.length - 1) {
      setCurrentQuestionIndex(qi + 1);
      resetTimer();
    } else {
      await finishQuizRef.current(merged);
    }
  };

  const advanceAfterRevealRef = useRef(advanceAfterReveal);
  advanceAfterRevealRef.current = advanceAfterReveal;

  useEffect(() => {
    if (!showAnswerFeedback || selectedAnswer === null || mcValidateResult === null) return;
    const id = window.setTimeout(() => {
      void advanceAfterRevealRef.current();
    }, WITC_REVEAL_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [showAnswerFeedback, selectedAnswer, mcValidateResult, currentQuestionIndex]);

  const finishQuiz = async (answersForSubmit: Map<number, string>) => {
    if (finishQuizInFlightRef.current) return;
    finishQuizInFlightRef.current = true;
    setSavingQuizAttempt(true);
    stopTimer();
    const finishedAt = new Date();
    setIsQuizCompleted(true);
    const duration = Math.max(0, Math.floor((finishedAt.getTime() - startTime.getTime()) / 1000));
    setResultDurationSeconds(duration);

    const bandForCorrect =
      soloQuickMatch &&
      playDifficultyPerQuestion &&
      playDifficultyPerQuestion.length === questions.length
        ? playDifficultyPerQuestion[0]
        : playDifficulty ?? quiz.difficulty ?? 'Easy';

    const responseTimes = Array.from({ length: questions.length }, (_, i) => {
      const limit =
        soloQuickMatch &&
        playDifficultyPerQuestion &&
        playDifficultyPerQuestion.length === questions.length
          ? witcTimeLimitSecondsForBand(playDifficultyPerQuestion[i])
          : embeddedInPvp
            ? witcTimeLimitSecondsForBand(playDifficulty ?? quiz.difficulty)
            : SOLO_TIME_LIMIT_SECONDS;
      return secondsToAnswerRef.current[i] ?? limit;
    });

    const answersArray = Array.from({ length: questions.length }, (_, index) =>
      answersForSubmit.get(index) || 'skip'
    );

    if (!mcEngineBootstrapCipher || !mcWasmReady) {
      finishQuizInFlightRef.current = false;
      setIsQuizCompleted(false);
      setSaveAttemptError(
        'Multiple Choice finish audit is missing. Reload the quiz and try again.'
      );
      setSavingQuizAttempt(false);
      return;
    }

    const mcFinishAudit = mcWasmBuildFinishPayload();
    if (!mcFinishAudit) {
      finishQuizInFlightRef.current = false;
      setIsQuizCompleted(false);
      setSaveAttemptError(
        'Multiple Choice finish audit is missing. Reload the quiz and try again.'
      );
      setSavingQuizAttempt(false);
      return;
    }

    const correctPQ = mcCorrectPerQuestionForSoloBand(questions, answersForSubmit, bandForCorrect);
    const skippedCount = answersArray.filter((a) => a === 'skip').length;
    const scoreForSubmit = mcFinishAudit.clientTotalScore ?? score;
    const correctForSubmit = correctPQ.filter(Boolean).length;

    setScore(scoreForSubmit);
    setCorrectAnswers(correctForSubmit);

    if (pvpAsync) {
      try {
        await pvpAsync.onComplete({
          score: scoreForSubmit,
          correctAnswers: correctForSubmit,
          totalQuestions: questions.length,
          durationSeconds: duration,
          skippedQuestions: skippedCount,
          questionIdsInOrder: questions.map((q) => q.id),
          answerIdsPerQuestion: questions.map((q) => q.answers.map((a) => a.id)),
          responseTimeSecondsPerQuestion: responseTimes,
          correctPerQuestion: correctPQ,
          mcFinishAudit,
        });
      } catch {
        toast.error(t('pvp.submitFailed', 'Failed to submit results'));
        finishQuizInFlightRef.current = false;
        setIsQuizCompleted(false);
      } finally {
        setSavingQuizAttempt(false);
      }
      return;
    }

    try {
      const saveQuizId =
        soloQuickMatch && questions.length > 0 ? questions[0].quizId : quizId;

      const response = await quizService.saveQuizAttempt(
        {
          quizId: saveQuizId,
          score: scoreForSubmit,
          correctAnswers: correctForSubmit,
          totalQuestions: questions.length,
          duration,
          isCompleted: true,
          answers: answersArray,
          questionIdsInOrder: questions.map((q) => q.id),
          responseTimeSecondsPerQuestion: responseTimes,
          playDifficulty: soloQuickMatch ? undefined : playDifficulty ?? quiz.difficulty,
          soloQuickMatch: soloQuickMatch || undefined,
          playDifficultiesPerQuestion:
            soloQuickMatch &&
            playDifficultyPerQuestion &&
            playDifficultyPerQuestion.length === questions.length
              ? playDifficultyPerQuestion
              : undefined,
          witcResults: soloQuickMatch
            ? Array.from({ length: questions.length }, () => ({ pointsEarned: 0 }))
            : undefined,
          mcFinishAudit,
        },
        { pwaClient: isPwaStandalone() }
      );

      setQuizResponse(response);
      if (response.tooManySkips) {
        setHasTooManySkips(true);
        setIsQuizCompleted(false);
      }
      if (response.earnedXP !== undefined) setEarnedXP(response.earnedXP);

      if (arenaPublishSnapshotRef) {
        arenaPublishSnapshotRef.current = null;
        if (
          !soloQuickMatch &&
          !response.tooManySkips &&
          questions.length > 0 &&
          correctForSubmit / questions.length >= 0.7 &&
          quiz
        ) {
          const bandSingle = playDifficulty ?? quiz.difficulty ?? 'Easy';
          arenaPublishSnapshotRef.current = {
            quizId: saveQuizId,
            playDifficulty: bandSingle,
            roomDisplayName: (quiz.title || 'Arena').trim().slice(0, 80),
            score: scoreForSubmit,
            correctAnswers: correctForSubmit,
            totalQuestions: questions.length,
            durationSeconds: duration,
            skippedQuestions: skippedCount,
            questionIdsInOrder: questions.map((q) => q.id),
            answerIdsPerQuestion: questions.map((q) => q.answers.map((a) => a.id)),
            responseTimeSecondsPerQuestion: responseTimes,
            correctPerQuestion: correctPQ,
            mcFinishAudit,
          };
        }
      }
      setArenaPublishChecked(true);

      const unlocked = response.unlockedAchievements?.filter(Boolean) ?? [];
      const unlockedChars = response.unlockedCharacters?.filter(Boolean) ?? [];
      const streakUpdate = response.loginStreakUpdated ?? null;
      const hasLevelUp = levelUpCelebration.prepareFromQuizResponse(response);
      if ((unlocked.length > 0 || unlockedChars.length > 0 || streakUpdate) && !response.tooManySkips) {
        setCelebrationAchievements(unlocked);
        setCelebrationCharacters(unlockedChars);
        setCelebrationLoginStreak(streakUpdate);
        setShowCelebration(true);
      } else if (hasLevelUp && levelUpCelebration.tryOpenLevelUpCelebration()) {
        /* level-up modal */
      } else {
        setShowResultsModal(true);
      }
    } catch (error: unknown) {
      finishQuizInFlightRef.current = false;
      setIsQuizCompleted(false);
      const msg =
        axios.isAxiosError(error) && error.response?.data
          ? String((error.response.data as { message?: string }).message ?? error.message)
          : error instanceof Error
            ? error.message
            : 'Unknown error';
      setSaveAttemptError(`Failed to save quiz results: ${msg}`);
    } finally {
      setSavingQuizAttempt(false);
    }
  };

  finishQuizRef.current = finishQuiz;

  const handleQuitQuiz = () => {
    if (embeddedInPvp && onPvpQuitRequest) {
      onPvpQuitRequest();
      return;
    }
    setShowQuitModal(true);
  };

  const confirmQuit = () => {
    stopTimer();
    const quitAt = new Date();
    const secs = Math.max(0, Math.floor((quitAt.getTime() - startTime.getTime()) / 1000));
    setResultDurationSeconds(secs);
    setIsQuizCompleted(false);
    setShowQuitModal(false);
    setShowResultsModal(true);
  };

  const handleBackToHome = async () => {
    await executeArenaPublishFromSoloIfNeeded();
    setShowResultsModal(false);
    setShowCelebration(false);
    levelUpCelebration.clearPendingLevelUp();
    navigate('/', { replace: true });
  };

  const handleCelebrationFinished = () => {
    setShowCelebration(false);
    setCelebrationAchievements([]);
    setCelebrationCharacters([]);
    setCelebrationLoginStreak(null);
    if (levelUpCelebration.tryOpenLevelUpCelebration()) {
      return;
    }
    setShowResultsModal(true);
  };

  const handleLevelUpFinished = () => {
    levelUpCelebration.closeLevelUpCelebration();
    setShowResultsModal(true);
  };

  if (mcEngineBootstrapCipher && mcWasmError) {
    return (
      <div className="quiz-page d-flex flex-column align-items-center justify-content-center min-vh-50 text-white">
        <Alert variant="danger" className="mx-3 text-center">
          {mcWasmError}
        </Alert>
      </div>
    );
  }

  if (!mcEngineBootstrapCipher || !mcWasmReady) {
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  if (saveAttemptError) {
    return (
      <div className="quiz-error">
        <Alert variant="danger">
          <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
          <p>{saveAttemptError}</p>
          <Button variant="outline-danger" onClick={() => void handleBackToHome()}>
            {t('quiz.backToHome', 'Back to Home')}
          </Button>
        </Alert>
      </div>
    );
  }

  const currentQuestion = mcQuestion;
  if (!currentQuestion) {
    return (
      <Alert variant="warning" className="m-3">
        {t('quiz.quizNotAvailable', 'This quiz is not available or has no questions.')}
      </Alert>
    );
  }

  const progressPct = ((currentQuestionIndex + 1) / questions.length) * 100;
  const imgSrc = mcImageUrls
    ? mcSoloImageExt === 'webp'
      ? mcImageUrls.webp
      : mcImageUrls.png
    : null;

  const gameplay = (
    <>
      <div className="quiz-header quiz-page-header">
        {!embeddedInPvp && <QuizPlayPageHeading quiz={quiz} language={i18n.language} />}
        <div className="quiz-page-header__meta d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex gap-2 align-items-center flex-wrap min-w-0 flex-grow-1">
            <Badge
              bg={
                headerDifficultyLabel === 'Easy'
                  ? 'success'
                  : headerDifficultyLabel === 'Medium'
                    ? 'warning'
                    : 'danger'
              }
            >
              {t(`quiz.${headerDifficultyLabel.toLowerCase()}`, headerDifficultyLabel)}
            </Badge>
            <span className="text-muted quiz-page-header__question-of">
              {t('quiz.questionOf', 'Question {{current}} of {{total}}')
                .replace('{{current}}', String(currentQuestionIndex + 1))
                .replace('{{total}}', String(questions.length))}
            </span>
            <Badge
              bg={timeLeft <= 10 ? 'danger' : timeLeft <= 20 ? 'warning' : 'info'}
              className="timer-badge"
            >
              {t('quiz.timeSeconds', '{{time}}s').replace('{{time}}', String(timeLeft))}
            </Badge>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            {!embeddedInPvp && <NavbarMusicToggle className="flex-shrink-0" />}
            {!embeddedInPvp && <QuizSoundToggleButton disabled={savingQuizAttempt} />}
            <Button
              type="button"
              variant="outline-danger"
              onClick={handleQuitQuiz}
              className="quiz-quit-icon-btn flex-shrink-0"
              aria-label={embeddedInPvp ? t('pvp.quitPvPQuiz', 'Quit PvP Quiz?') : t('quiz.quitQuiz', 'Quit Quiz')}
              title={embeddedInPvp ? t('pvp.quitPvPQuiz', 'Quit PvP Quiz?') : t('quiz.quitQuiz', 'Quit Quiz')}
            >
              <i className="bi bi-x-lg" aria-hidden />
            </Button>
          </div>
        </div>
      </div>

      <ProgressBar now={progressPct} className="quiz-progress-bar" label={`${Math.round(progressPct)}%`} />

      <Card className="question-card quiz-compact">
        <Card.Body>
          <Card.Title
            as="h3"
            className={`question-title ${imgSrc ? 'question-title--above-image' : ''}`}
          >
            {pickBilingualLine(currentQuestion.text, currentQuestion.textEn, i18n.language)}
          </Card.Title>
          <p className="small mb-2 fw-medium quiz-points-hint witc-points-left--full">
            {i18n.t('witc.pointsForQuestion', { count: pointsThisQuestion })}
          </p>
          {imgSrc ? (
            <div
              className="mc-question-image-panel mb-3 rounded overflow-hidden position-relative d-flex align-items-center justify-content-center"
              style={{ maxHeight: 320, minHeight: 200, backgroundColor: mcPanelBg }}
            >
              <Button
                type="button"
                variant="light"
                size="sm"
                onClick={onQuizSkipClick(handleSkipQuestion)}
                disabled={selectedAnswer !== null || savingQuizAttempt || mcValidating}
                className="witc-skip-on-image-btn d-md-none"
                aria-label={
                  embeddedInPvp
                    ? t('pvp.skipPvPQuestion', 'Skip Question')
                    : t('quiz.skipQuestionBtn', 'Skip Question')
                }
                title={
                  embeddedInPvp
                    ? t('pvp.skipPvPQuestion', 'Skip Question')
                    : t('quiz.skipQuestionBtn', 'Skip Question')
                }
              >
                <i className="bi bi-skip-forward" aria-hidden />
              </Button>
              <img
                src={imgSrc}
                alt=""
                className="mc-question-image witc-image-silhouette d-block"
                decoding="async"
                onError={() => {
                  if (mcSoloImageExt === 'webp') setMcSoloImageExt('png');
                }}
              />
            </div>
          ) : null}

          <div
            className={`answers-container${imgSrc ? ' answers-container--after-question-image' : ''}${mcUsesImageAnswers ? ' answers-container--image-options' : ''}`}
          >
            {currentQuestion.answers.map((answer) => {
              const revealWrong =
                showAnswerFeedback && mcValidateResult !== null && !mcValidateResult.correct;
              const isRevealedCorrect =
                revealWrong && mcValidateResult?.correctAnswerId === answer.id;
              const pickedWrong =
                revealWrong && selectedAnswer === answer.id && !mcValidateResult?.correct;
              const variant = isRevealedCorrect
                ? 'success'
                : selectedAnswer === answer.id
                  ? pickedWrong
                    ? 'danger'
                    : 'primary'
                  : 'outline-primary';
              const answerLabel = pickBilingualLine(answer.text, answer.textEn, i18n.language);
              const answerImageKey = answer.imageAssetKey?.trim();
              if (mcAnswerImageBase && answerImageKey) {
                return (
                  <McImageAnswerButton
                    key={answer.id}
                    basePath={mcAnswerImageBase}
                    imageAssetKey={answerImageKey}
                    variant={variant}
                    className={selectedAnswer === answer.id ? 'selected' : ''}
                    onClick={() => void handleAnswerSelect(answer.id)}
                    disabled={selectedAnswer !== null || savingQuizAttempt || mcValidating}
                    showCorrectIcon={Boolean(isRevealedCorrect)}
                    fallbackLabel={answerLabel}
                  />
                );
              }
              return (
                <Button
                  key={answer.id}
                  variant={variant}
                  className={`answer-button ${selectedAnswer === answer.id ? 'selected' : ''}`}
                  onClick={() => void handleAnswerSelect(answer.id)}
                  disabled={selectedAnswer !== null || savingQuizAttempt || mcValidating}
                >
                  {isRevealedCorrect ? (
                    <i className="bi bi-check-circle-fill me-2" aria-hidden />
                  ) : null}
                  {answerLabel}
                </Button>
              );
            })}
          </div>

          {showAnswerFeedback && selectedAnswer && mcValidateResult !== null && (
            <div className="answer-feedback">
              <Alert variant={mcValidateResult.correct ? 'success' : 'danger'}>
                <i
                  className={`bi ${mcValidateResult.correct ? 'bi-check-circle' : 'bi-x-circle'}`}
                />
                {mcValidateResult.correct
                  ? ` ${embeddedInPvp ? t('pvp.correctPvPAnswer', 'Correct answer!') : t('quiz.correctAnswer', 'Correct answer!')}`
                  : ` ${embeddedInPvp ? t('pvp.incorrectPvPAnswer', 'Incorrect answer.') : t('quiz.incorrectAnswer', 'Incorrect answer.')}`}
              </Alert>
            </div>
          )}

          <div className="quiz-stats-actions-container">
            <div className="quiz-stats-inline">
              <div className="stat-line">
                {embeddedInPvp ? t('pvp.scorePvP', 'Score') : t('quiz.score', 'Score')}: {score}
              </div>
              <div className="stat-line">
                {embeddedInPvp ? t('pvp.correctPvPAnswers', 'Correct') : t('quiz.correctAnswers', 'Correct')}:{' '}
                {correctAnswers}/{questions.length}
              </div>
            </div>
            <div className="quiz-actions quiz-footer-actions">
              <Button
                variant="outline-secondary"
                onClick={onQuizSkipClick(handleSkipQuestion)}
                className={`quiz-action-btn align-items-center${imgSrc ? ' d-none d-md-inline-flex' : ''}`}
                disabled={selectedAnswer !== null || savingQuizAttempt || mcValidating}
              >
                <i className="bi bi-skip-forward me-1 me-sm-2" />
                {embeddedInPvp
                  ? t('pvp.skipPvPQuestion', 'Skip Question')
                  : t('quiz.skipQuestionBtn', 'Skip Question')}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {savingQuizAttempt && (
        <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="quiz-finish-saving-overlay__inner">
            <Spinner animation="border" role="presentation" />
            <span className="text-white mt-2 small">
              {embeddedInPvp
                ? t('pvp.submitting', 'Submitting...')
                : t('quiz.savingResults', 'Saving results...')}
            </span>
          </div>
        </div>
      )}
    </>
  );

  if (embeddedInPvp) {
    return <div className="mc-quiz-player-embedded mc-quiz-player">{gameplay}</div>;
  }

  return (
    <div className="quiz-page">
      <div className="quiz-container">
        {gameplay}

        <QuizFinishCelebrationModal
          show={showCelebration}
          achievements={celebrationAchievements}
          characters={celebrationCharacters}
          loginStreak={celebrationLoginStreak}
          onFinished={handleCelebrationFinished}
          playerNickname={celebrationPlayerNick}
        />

        <LevelUpCelebrationModal
          show={levelUpCelebration.showLevelUp}
          oldLevel={levelUpCelebration.levelUpOld}
          newLevel={levelUpCelebration.levelUpNew}
          onClose={handleLevelUpFinished}
        />

        <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('quiz.quitQuizModal', 'Quit Quiz?')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t(
              'quiz.quitQuizConfirm',
              'Are you sure you want to quit this quiz? Your progress will be lost and no points will be awarded.'
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
              {t('quiz.continueQuiz', 'Continue Quiz')}
            </Button>
            <Button variant="danger" onClick={confirmQuit}>
              {t('quiz.quitQuiz', 'Quit Quiz')}
            </Button>
          </Modal.Footer>
        </Modal>

        <SoloQuizResultsModal
          show={showResultsModal && !suppressResultsModal}
          hasTooManySkips={hasTooManySkips}
          isQuizCompleted={isQuizCompleted}
          quizTitleDisplay={getQuizFullDisplayCaption(quiz, i18n.language)}
          correctAnswers={correctAnswers}
          totalQuestions={questions.length}
          score={score}
          earnedXP={earnedXP}
          quizResponse={quizResponse}
          resultDurationSeconds={resultDurationSeconds}
          onPlayAgain={onPlayAgain}
          onBackToHome={() => void handleBackToHome()}
          playAgainLoading={playAgainLoading}
          arenaPublishOffer={arenaPublishOffer}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishAvailabilityLoading={arenaHostedSummaryLoading}
          arenaPublishBlocked={arenaPublishBlocked}
          arenaPublishBlockedReason={arenaPublishBlockedReason}
          arenaPublishMaxOpenRooms={hostedOpenSummaryForArena?.maxOpenRooms}
        />
      </div>
    </div>
  );
};

export default McQuizPlayer;
